import { c as _c } from "react/compiler-runtime";
import { SquareArrowOutUpRight, X } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { BetterTooltip } from '@/components/ui/tooltip';
type BaseIssueProps<IssueType> = {
  providerLabel: string;
  SearchComponent: ReactNode;
  issue: IssueType | null;
  isError: boolean;
  isLoading: boolean;
  onOpenProvider: (issue: IssueType) => void;
  /** Omit for read-only links that are managed in the provider itself. */
  onUnlink?: () => void;
  isUnlinking?: boolean;
  getIssueKey: (issue: IssueType) => string;
  getIssueSummary: (issue: IssueType) => string;
  tooltipContent: (issue: IssueType) => string | ReactElement;
  showIssueKey?: boolean;
};
export function BaseIssue(t0) {
  const $ = _c(20);
  const {
    providerLabel,
    SearchComponent,
    issue,
    isError,
    isLoading,
    onOpenProvider,
    onUnlink,
    isUnlinking: t1,
    getIssueKey,
    getIssueSummary,
    tooltipContent,
    showIssueKey: t2
  } = t0;
  const isUnlinking = t1 === undefined ? false : t1;
  const showIssueKey = t2 === undefined ? true : t2;
  if (isLoading) {
    return null;
  }
  let t3;
  if ($[0] !== providerLabel) {
    t3 = <span className="text-xs block text-zinc-400">{providerLabel}</span>;
    $[0] = providerLabel;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== SearchComponent || $[3] !== t3) {
    t4 = <div className="flex items-center justify-between mb-1.5">{t3}{SearchComponent}</div>;
    $[2] = SearchComponent;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== getIssueKey || $[6] !== getIssueSummary || $[7] !== isError || $[8] !== isUnlinking || $[9] !== issue || $[10] !== onOpenProvider || $[11] !== onUnlink || $[12] !== showIssueKey || $[13] !== tooltipContent) {
    t5 = !isError && issue ? <><BetterTooltip side="bottom" content={tooltipContent(issue) || ""}><button type="button" className="flex items-center min-w-0 flex-1 cursor-pointer transition-colors hover:text-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500" onClick={e => {
          e.stopPropagation();
          onOpenProvider(issue);
        }}>{showIssueKey && <span className="mr-1 text-zinc-400 shrink-0">{getIssueKey(issue)}</span>}<span className="truncate text-zinc-300">{getIssueSummary(issue)}</span></button></BetterTooltip>{onUnlink ? <Button type="button" variant="ghost" size="icon" className="h-6 w-0 shrink-0 overflow-hidden text-zinc-500 opacity-0 transition-all hover:text-zinc-300 focus-visible:w-6 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 group-hover:w-6 group-hover:opacity-100" aria-label="Unlink issue" onClick={e_0 => {
        e_0.stopPropagation();
        onUnlink();
      }} disabled={isUnlinking}><X className="w-3 h-3" /></Button> : null}</> : null;
    $[5] = getIssueKey;
    $[6] = getIssueSummary;
    $[7] = isError;
    $[8] = isUnlinking;
    $[9] = issue;
    $[10] = onOpenProvider;
    $[11] = onUnlink;
    $[12] = showIssueKey;
    $[13] = tooltipContent;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <div className="flex items-center justify-between text-sm text-zinc-200 min-w-0 w-full group relative">{t5}</div>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t4 || $[18] !== t6) {
    t7 = <div className="mt-6 w-full md:w-[220px]">{t4}{t6}</div>;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
export function IssueTooltipContent(t0) {
  const $ = _c(8);
  const {
    label,
    summary
  } = t0;
  let t1;
  if ($[0] !== summary) {
    t1 = <div className="font-medium">{summary}</div>;
    $[0] = summary;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `Open in ${label}`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SquareArrowOutUpRight className="w-3 h-3" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = <div className="flex items-center gap-1 text-xs text-muted-foreground">{t2}{t3}</div>;
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t4) {
    t5 = <div className="space-y-1">{t1}{t4}</div>;
    $[5] = t1;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}